import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';

/**
 * Work that a page reload would cut short, held open while it runs: `OfflineShellService` never
 * applies the reload onto a new build while anything holds the gate. The offer (the update banner)
 * stays up meanwhile, only its click is refused; it works again after the last release.
 * Dependency-free on purpose, so any service can hold it -- today Scan's offline downloads
 * (`ScanOfflineService`), whose round would otherwise be restarted mid-part.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateGate {
  private holds = 0;
  private readonly heldSubject = new BehaviorSubject(false);

  /** Whether some work holds the gate, current value first, emitted on every change. */
  readonly held$: Observable<boolean> = this.heldSubject.pipe(distinctUntilChanged());

  /** True while some work is holding the gate. */
  get held(): boolean {
    return this.holds > 0;
  }

  /** Hold the gate; call the returned function once to release (extra calls are ignored). */
  hold(): () => void {
    this.holds++;
    this.heldSubject.next(true);
    let released = false;
    return () => {
      if (!released) {
        released = true;
        this.holds--;
        this.heldSubject.next(this.holds > 0);
      }
    };
  }
}
